import { ILogObjMeta, IMeta, Logger as TSLogger } from "tslog";
import { type JsonObject } from "type-fest";
import { getEnvironment, type Environment } from "./environment";

type Level = "TRACE" | "DEBUG" | "INFO" | "WARN" | "ERROR" | "FATAL";
const LEVEL = {
  TRACE: 0,
  DEBUG: 1,
  INFO: 2,
  WARN: 3,
  ERROR: 4,
  FATAL: 5,
} as const satisfies Record<Level, number>;

type LogContext = {
  error?: Error;
  metadata?: JsonObject;
};

type StructuredLog = {
  message: string;
  error?: Error;
  metadata?: JsonObject;
};

const getMinLevel = (environment: Environment): number => {
  switch (environment) {
    case "production":
    case "relcert":
      return LEVEL.WARN;
    case "staging":
      return LEVEL.DEBUG;
    case "development":
    default:
      return LEVEL.TRACE;
  }
};

const getError = (
  error: Error,
): { message: string; name: string; trace?: string; cause?: Error } => ({
  name: error.name,
  message: error.message,
  trace: error.stack,
  cause: error.cause instanceof Error ? getError(error.cause) : undefined,
});

const getCircularReplacer = () => {
  const seen = new WeakMap<any, string>();
  return (key: string, value: unknown): unknown | undefined => {
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) {
        const originalKey = seen.get(value);
        return `[Circular ~${originalKey}]`;
      }
      seen.set(value, key);
    }
    return value;
  };
};

type LoggerContructorArgs = {
  /**
   * @summary The name of the application, often times the repo name
   * @example "Offer Experience", "Sales Experience"
   */
  application: string;
  /**
   * @summary Additional context about the error location
   * @example "Offer Create", "Lead Generation"
   */
  context?: string;
  environment?: Environment;
  runtime: "web" | "node";
  transports?: Array<(lobObj: StructuredLog & ILogObjMeta) => void>;
};

/**
 * @summary
 * Utility to handle structured logging.
 *
 * @description
 * Works in both Node and Browsers.
 *
 * LEVELS:
 * 6. FATAL
 * 5. ERROR
 * 4. WARN (production, relcert)
 * 3. INFO
 * 2. DEBUG (staging)
 * 1. TRACE (development)
 *
 * @example
 * ```ts
 * // browser config
 * const logger = new Logger({
 *   application: "Offer Experience",
 *   runtime: "web",
 * });
 * logger.info("I am logging!");
 * ```
 *
 * @example
 * ```ts
 * // node config
 * const logger = new Logger({
 *   application: "Offer Experience",
 *   context: "CheckoutAPI"
 *   runtime: "node",
 * });
 * logger.error("I am logging!");
 * ```
 */
class Logger {
  static readonly LEVEL = LEVEL;

  #tslogger: TSLogger<StructuredLog>;

  constructor({
    application,
    context,
    environment,
    runtime,
    transports,
  }: LoggerContructorArgs) {
    const name = `[${application}]${context ? `[${context}]` : ""}`;
    this.#tslogger = new TSLogger<StructuredLog>({
      hideLogPositionForProduction: true,
      minLevel: getMinLevel(environment ?? getEnvironment()),
      name,
      stylePrettyLogs: false,
      type: runtime === "node" ? "json" : "pretty",
      attachedTransports: transports,
      overwrite: {
        mask: (args: Array<unknown>): Array<unknown> => args,
        toLogObj: (args: unknown[]): StructuredLog => {
          const [first, second] = args;
          const logContext = (second ?? undefined) as LogContext | undefined;
          if (typeof first === "string") {
            return {
              message: first,
              error: logContext?.error,
              metadata: logContext?.metadata,
            };
          }
          if (first instanceof Error) {
            return {
              message: first.message,
              error: first,
              metadata: logContext?.metadata,
            };
          }
          return {
            // NOTE: last ditch effort to keep log
            message: JSON.stringify(args, getCircularReplacer()),
            metadata: {
              ...logContext?.metadata,
              devMessage: "This is an unsupported way to call logger!",
            },
          };
        },
        addMeta(logObj, logLevelId, logLevelName) {
          return {
            ...logObj,
            _meta: {
              application,
              context,
              date: new Date(),
              logLevelId,
              logLevelName,
              name,
            },
          } as any;
        },
        formatMeta:
          runtime !== "web"
            ? undefined
            : (meta?: IMeta | undefined) => {
                if (!meta) return "";
                return `${meta.logLevelName} ${
                  meta.name ? `${meta.name} ` : ""
                }%s`;
              },
        formatLogObj: (maskedArgs: Array<unknown>) => ({
          args: maskedArgs,
          errors: [],
        }),
        transportFormatted: (logMetaMarkup: string, logArgs: unknown[]) => {
          const message = logMetaMarkup.replace("%s", logArgs[0] as string);
          const logContext = logArgs[1] as LogContext;
          const consoleArgs = [message, logContext].filter((e) => e);
          const level = logMetaMarkup.match(/^\w+/)?.[0] as Level;
          /* eslint-disable no-console */
          switch (level) {
            case "FATAL":
            case "ERROR":
              console.error(...consoleArgs);
              break;
            case "WARN":
              console.warn(...consoleArgs);
              break;
            case "INFO":
              console.info(...consoleArgs);
              break;
            case "DEBUG":
            case "TRACE":
              console.debug(...consoleArgs);
              break;
            default:
              console.log(...consoleArgs);
              break;
          }
          /* eslint-enable no-console */
        },
        transportJSON: (json: unknown) => {
          if (typeof json !== "object" || json === null) return;
          const output = {
            ...json,
          };
          if ("error" in output && output.error instanceof Error) {
            output.error = getError(output.error);
          }
          // eslint-disable-next-line no-underscore-dangle
          const level = (json as any)._meta.logLevelName as Level;
          /* eslint-disable no-console */
          switch (level) {
            case "FATAL":
            case "ERROR":
              console.error(JSON.stringify(output, getCircularReplacer()));
              break;
            case "WARN":
              console.warn(JSON.stringify(output, getCircularReplacer()));
              break;
            case "INFO":
              console.info(JSON.stringify(output, getCircularReplacer()));
              break;
            case "DEBUG":
            case "TRACE":
              console.debug(JSON.stringify(output, getCircularReplacer()));
              break;
            default:
              console.log(JSON.stringify(output, getCircularReplacer()));
              break;
          }
          /* eslint-enable no-console */
        },
      },
    });
  }

  /**
   * @description
   * Verbose logs that could be costly to store.
   * Only turn on if you need extremely granular information to debug a system.
   *
   * INFO: Enabled in development by default.
   *
   * @example
   * ```ts
   * logger.trace("ProspectId:", { metadata: { prospectId }});
   * logger.trace("UserProfile:", { metadata: { userProfile }});
   * logger.trace("Entered myFunction()", { metadata: { arg1, arg2 }});
   * logger.trace("Middle of myFunction()", { metadata: { arg1, arg2 }});
   * logger.trace("Exited myFunction()", { metadata: { arg1, arg2 }});
   * ```
   */
  trace(value: any, context?: LogContext): StructuredLog {
    return this.#tslogger.trace(value, context) as any as StructuredLog;
  }

  /**
   * @description
   * Fine-grained informational events that are most useful to debug an
   * application.
   * - Complete HTTP request and response
   * - Intermediate values of complicated logic
   *
   * INFO: Enabled in development & staging by default.
   *
   * @example
   * ```ts
   * logger.debug("Network Request", { metadata: { prospectId, request }});
   * logger.debug("Network Response", { metadata: { prospectId, request, response }});
   * ```
   */
  debug(value: any, context?: LogContext): StructuredLog {
    return this.#tslogger.debug(value, context) as any as StructuredLog;
  }

  /**
   * @description
   * Routine application events such as:
   * - Summaries for request and response for third-party services
   * - Results of business logic determinations & heuristics
   * - Service input
   * - Service output
   *
   * INFO: Enabled in development & staging by default.
   *
   * @example
   * ```ts
   * logger.info("Network response 200", { metadata: { prospectId, request, response }});
   * ```
   */
  info(value: any, context?: LogContext): StructuredLog {
    return this.#tslogger.info(value, context) as any as StructuredLog;
  }

  /**
   * @description
   * Any state or events that could indicate something is wrong, but doesn't
   * halt the program.
   * - Response from third-party changed unexpectedly
   * - A flaky service call needed to be retried
   *
   * INFO: Enabled in development & staging & production by default.
   *
   * @example
   * ```ts
   * logger.warn("Network call was slow", { metadata: { prospectId, duration, url }});
   * ```
   */
  warn(value: string | Error, context?: LogContext): StructuredLog {
    return this.#tslogger.warn(value, context) as any as StructuredLog;
  }

  /**
   * @description
   * Any state or events that are serious problems that need to be handled.
   * - Unable to submit data to reporting data-lake
   * - Browser app not connected to the internet
   *
   * INFO: Enabled in development & staging & production by default.
   *
   * @example
   * ```ts
   * logger.error("Rollbar failed to initialize");
   * logger.error("Unable to get usage data", { metadata: { offerId, prospectId }});
   * logger.error("Network response 500", { metadata: { prospectId, status, url }});
   * logger.error(error, { metadata: { prospectId }});
   * logger.error("This message is better than the error", { error: error, metadata: { prospectId }});
   * ```
   */
  error(value: string | Error, context?: LogContext): StructuredLog {
    return this.#tslogger.error(value, context) as any as StructuredLog;
  }

  /**
   * @description
   * Any problem that results in the program halting or crashing.
   * - Runtime error such as syntax or type errors
   * - Failure to connect to a required database or third-party service
   * - Any other problem that could result in serious financial loss for the
   *   company
   *
   * INFO: Enabled in development & staging & production by default.
   *
   * @example
   * ```ts
   * logger.fatal("Prospect Id is missing and needed");
   * logger.fatal("Failed to save lead", { metadata: { channel }});
   * logger.fatal("SFDC endpoint failed to fetch Prospect", { metadata: { prospectId }});
   * logger.fatal(error, { metadata: { prospectId }});
   * logger.fatal("This message is better than the error", { error: error, metadata: { prospectId }});
   * ```
   */
  fatal(value: string | Error, context?: LogContext): StructuredLog {
    return this.#tslogger.fatal(value, context) as any as StructuredLog;
  }
}

export {
  Logger,
  type LoggerContructorArgs,
  LEVEL,
  type Level,
  type StructuredLog,
};
